/* Universal01 optional add-ons. Turbo remains responsible for the cart request and drawer. */
(() => {
  'use strict';

  if (window.BPLProductAddons) {
    window.BPLProductAddons.init(document);
    return;
  }

  const selector = '.bpl-product--01 [data-bpl-addons]';
  const states = new Map();
  let cart = null;
  let cartRequest = null;
  let cartHook = false;
  let cartRevision = 0;
  let refreshAfterRequest = false;
  const nativeRequests = new WeakMap();
  const pendingMutations = new Set();
  let readSequence = 0;
  let acceptedReadSequence = 0;

  function integer(value, minimum = 0) {
    const result = Number(value);
    return Number.isSafeInteger(result) && result >= minimum ? result : null;
  }

  function money(state, value) {
    const format = state.wrapper.dataset.moneyFormat;
    if (format && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(value, format); } catch (_) { /* Use currency fallback. */ }
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: state.currency }).format(value / 100);
  }

  function text(root, name, value) {
    root.querySelectorAll(`[data-bpl-${name}]`).forEach(node => {
      const next = String(value);
      if (node.textContent !== next) node.textContent = next;
    });
  }

  function visible(root, name, show) {
    root.querySelectorAll(`[data-bpl-${name}]`).forEach(node => { node.hidden = !show; });
  }

  function status(state, message) {
    text(state.panel, 'status', message);
    visible(state.panel, 'status', Boolean(message));
  }

  function cartURL(state) {
    try {
      const root = new URL(state.panel.dataset.root || '/', window.location.origin);
      if (root.origin !== window.location.origin || root.search || root.hash) return null;
      if (!root.pathname.endsWith('/')) root.pathname += '/';
      return new URL('cart.js', root).href;
    } catch (_) { return null; }
  }

  function validCart(value) {
    return value && integer(value.item_count) !== null && Array.isArray(value.items);
  }

  function acceptCart(value) {
    if (!validCart(value)) return false;
    cart = value;
    states.forEach(state => {
      state.cartFailed = false;
      if (state.awaitingCart) { state.awaitingCart = false; state.error = ''; }
      if (!pendingMutations.size && state.confirmation) {
        const confirmation = state.confirmation;
        if (confirmation.mainIncluded && confirmation.mainRevision === state.mainRevision) state.forceMain = false;
        confirmation.extras.forEach(entry => {
          if (entry.item.editVersion === entry.editVersion) entry.item.input.checked = false;
        });
        if (confirmation.version === state.selectionVersion) state.phase = 'cart';
        state.confirmation = null;
      }
      schedule(state);
      finishCheckout(state);
    });
    return true;
  }

  function readCart(state, fresh = false) {
    if (pendingMutations.size) {
      refreshAfterRequest = true;
      return Promise.resolve(false);
    }
    if (cartRequest) {
      if (fresh) refreshAfterRequest = true;
      return cartRequest;
    }
    const url = cartURL(state);
    if (!url) {
      state.cartFailed = true;
      state.checkoutIntent = null;
      schedule(state);
      return Promise.resolve(false);
    }
    const revision = cartRevision;
    const sequence = ++readSequence;
    refreshAfterRequest = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    cartRequest = window.fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(response => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then(value => {
        if (controller.signal.aborted) throw new Error('Cart read timed out');
        if (revision !== cartRevision || sequence < acceptedReadSequence) return true;
        if (!acceptCart(value)) throw new Error('Cart unavailable');
        acceptedReadSequence = sequence;
        return true;
      })
      .catch(() => {
        if (revision !== cartRevision || sequence < acceptedReadSequence) return false;
        cart = null;
        states.forEach(item => { item.cartFailed = true; item.checkoutIntent = null; schedule(item); });
        return false;
      })
      .finally(() => {
        window.clearTimeout(timeout);
        cartRequest = null;
        if (refreshAfterRequest && states.size && !pendingMutations.size) {
          refreshAfterRequest = false;
          readCart(states.values().next().value);
        }
      });
    return cartRequest;
  }

  function cartPath(url) {
    try {
      const target = new URL(url, window.location.origin);
      return target.origin === window.location.origin && /\/cart(?:\/(?:add|change|update|clear))?\.js$/.test(target.pathname);
    } catch (_) { return false; }
  }

  function bindCartHook() {
    if (cartHook || !window.jQuery) return;
    cartHook = true;
    window.jQuery(document).on('ajaxSend.bplProductAddons', (_event, request, settings) => {
      if (!states.size || !cartPath(settings.url)) return;
      const action = new URL(settings.url, window.location.origin).pathname.match(/\/cart\/(add|change|update|clear)\.js$/);
      const mutation = Boolean(action);
      let attempt = null;
      if (mutation) {
        cartRevision += 1;
        pendingMutations.add(request);
        invalidateCart();
        if (action[1] === 'add') {
          const payload = requestItems(settings.data);
          if (payload) states.forEach(state => {
            if (!attempt && state.attempt && state.attempt.payload === payload) {
              attempt = { ...state.attempt, state, payload };
              state.inFlight = attempt;
              clearAttempt(state);
            }
          });
        }
      }
      nativeRequests.set(request, { revision: cartRevision, sequence: mutation ? 0 : ++readSequence, mutation, attempt });
    });
    window.jQuery(document).on('ajaxComplete.bplProductAddons', (_event, request, settings) => {
      if (!states.size || !cartPath(settings.url)) return;
      const meta = nativeRequests.get(request);
      nativeRequests.delete(request);
      const mutation = meta ? meta.mutation : /\/cart\/(add|change|update|clear)\.js$/.test(new URL(settings.url, window.location.origin).pathname);
      const succeeded = request.status >= 200 && request.status < 300 && !(request.responseJSON && Number(request.responseJSON.status) >= 400);
      if (mutation) {
        pendingMutations.delete(request);
        cartRevision += 1;
        invalidateCart();
        if (meta && meta.attempt && states.has(meta.attempt.state.panel)) {
          const state = meta.attempt.state;
          state.inFlight = null;
          const added = succeeded && validAddResult(request.responseJSON, meta.attempt.payload);
          if (added) state.confirmation = meta.attempt;
          if (meta.attempt.version === state.selectionVersion) {
            state.addFailed = !added;
            state.addedVersion = added ? meta.attempt.version : null;
            // Even Shopify's 422 inventory response can add part of a request.
            // Never retry a failed/partial request as another full add.
            state.uncertainVersion = !added ? meta.attempt.version : null;
            if (!added) state.checkoutIntent = null;
            state.checkoutMessage = state.uncertainVersion !== null ? 'Please review your cart before trying again.' : '';
          }
          if (!added) {
            state.addFailed = true;
            state.uncertainVersion = state.selectionVersion;
            state.checkoutIntent = null;
            state.checkoutMessage = 'Please review your cart before trying again.';
          }
        }
        // A read started before this mutation finished cannot confirm the result.
        // Wait for all observed mutations before taking the final cart snapshot.
        readCart(states.values().next().value, true);
      } else if (meta && meta.revision === cartRevision && meta.sequence >= acceptedReadSequence && !pendingMutations.size && succeeded && validCart(request.responseJSON)) {
        acceptCart(request.responseJSON);
        acceptedReadSequence = meta.sequence;
      } else if (meta && meta.revision === cartRevision && meta.sequence >= acceptedReadSequence && !pendingMutations.size && (!succeeded || !validCart(request.responseJSON))) {
        cart = null;
        states.forEach(state => { state.cartFailed = true; state.checkoutIntent = null; schedule(state); });
      }
      states.forEach(schedule);
    });
  }

  function invalidateCart() {
    cart = null;
    states.forEach(state => { state.cartFailed = false; schedule(state); });
  }

  function requestItems(data) {
    try {
      if (typeof data === 'string' && data.trim().startsWith('{')) data = JSON.parse(data);
      if (data && Array.isArray(data.items)) return itemFingerprint(data.items);
      const entries = typeof data === 'string' ? Array.from(new URLSearchParams(data)) : data && typeof data.entries === 'function' ? Array.from(data.entries()) : data && typeof data === 'object' ? Object.entries(data) : [];
      const groups = new Map();
      const single = {};
      for (const [name, value] of entries) {
        if (name === 'id' || name === 'quantity') {
          if (Object.prototype.hasOwnProperty.call(single, name)) return null;
          single[name] = value;
        }
        const match = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(name);
        if (!match) continue;
        const index = Number(match[1]);
        const group = groups.get(index) || {};
        if (Object.prototype.hasOwnProperty.call(group, match[2])) return null;
        group[match[2]] = value;
        groups.set(index, group);
      }
      if (groups.size && Object.keys(single).length) return null;
      return itemFingerprint(groups.size ? Array.from(groups).sort((a, b) => a[0] - b[0]).map(entry => entry[1]) : [single]);
    } catch (_) { return null; }
  }

  function itemFingerprint(items) {
    if (!items.length) return null;
    const values = [];
    for (const item of items) {
      const id = String(item.id || '');
      const quantity = integer(item.quantity === undefined ? 1 : item.quantity, 1);
      if (!/^\d+$/.test(id) || quantity === null) return null;
      values.push([id, quantity]);
    }
    return JSON.stringify(values);
  }

  function validAddResult(value, payload) {
    if (!value || value.item_count !== undefined) return false;
    const items = Array.isArray(value.items) ? value.items : value.id ? [value] : [];
    return JSON.parse(payload).every(([id, quantity]) => items.some(item => String(item.id) === id && integer(item.quantity, 1) !== null && Number(item.quantity) >= quantity));
  }

  function clearAttempt(state) {
    state.attempt = null;
    if (state.attemptTimer) window.clearTimeout(state.attemptTimer);
    state.attemptTimer = 0;
  }

  function armAttempt(state, event) {
    clearAttempt(state);
    if (event.defaultPrevented || event.prevented || state.button.disabled || state.button.classList.contains('disabled')) return;
    const plan = state.prepared ? state.prepared.plan : pendingPlan(state);
    if (!plan || unsupportedPlan(state)) return;
    state.attempt = { version: state.selectionVersion, payload: itemFingerprint(plan.items), mainIncluded: plan.mainIncluded,
      mainRevision: state.mainRevision, extras: plan.extras.map(item => ({ item, editVersion: item.editVersion })) };
    // Turbo serializes in the native event turn. Unmatched/later requests cannot
    // be mistaken for this form's successful add.
    state.attemptTimer = window.setTimeout(() => {
      clearAttempt(state);
      if (!state.inFlight) state.checkoutIntent = null;
      schedule(state);
    }, 0);
  }

  function editSelection(state, mainChanged = false) {
    clearAttempt(state);
    const uncertain = state.uncertainVersion !== null;
    if (mainChanged) { state.mainRevision += 1; state.forceMain = true; }
    state.selectionVersion += 1;
    state.phase = 'pending';
    state.addFailed = uncertain;
    state.error = '';
    state.checkoutIntent = null;
    state.addedVersion = null;
    state.uncertainVersion = uncertain ? state.selectionVersion : null;
    state.checkoutMessage = uncertain ? 'Please review your cart before trying again.' : '';
  }

  function destination(state, path) {
    const url = cartURL(state);
    return url ? new URL(path, url).href : null;
  }

  function checkoutBusy(state) {
    return Boolean(state.inFlight || state.attempt || pendingMutations.size || state.navigating || (state.checkoutIntent && state.addedVersion === state.selectionVersion && cartRequest));
  }

  function goToCheckout(state, path = 'checkout') {
    const url = destination(state, path);
    if (!url || state.navigating || !states.has(state.panel) || !state.panel.isConnected) return;
    state.navigating = true;
    state.checkoutIntent = null;
    syncCheckout(state);
    window.location.assign(url);
  }

  function finishCheckout(state) {
    const intent = state.checkoutIntent;
    if (!intent || intent.version !== state.selectionVersion || state.addedVersion !== state.selectionVersion || state.inFlight || state.attempt || pendingMutations.size || state.navigating || state.cartFailed) return;
    const amounts = cartAmounts(state);
    if (!amounts || amounts.count < 1) {
      state.checkoutIntent = null;
      return;
    }
    goToCheckout(state);
  }

  function syncCheckout(state) {
    if (!state.checkoutButton) return;
    const unsupported = unsupportedPlan(state);
    const busy = checkoutBusy(state);
    const added = state.phase === 'cart' || state.addedVersion === state.selectionVersion;
    const uncertain = state.uncertainVersion === state.selectionVersion;
    const amounts = cartAmounts(state);
    const plan = pendingPlan(state);
    state.checkoutButton.hidden = unsupported;
    state.checkoutButton.disabled = Boolean(unsupported || !destination(state, 'checkout') || busy || (!added && !uncertain && (!plan || state.button.disabled || state.button.classList.contains('disabled'))) || (added && amounts && amounts.count === 0));
    const label = state.navigating ? 'Opening…' : busy ? (added && !state.inFlight ? 'Checking cart…' : 'Adding…') : uncertain ? 'Review cart' : added ? 'Checkout' : 'Add & checkout';
    if (state.checkoutButton.textContent !== label) state.checkoutButton.textContent = label;
    state.checkoutButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    const failedRead = added && (state.cartFailed || (cart && !amounts));
    const message = state.checkoutMessage || (failedRead ? 'Your items were added. Click Checkout to check your cart again.' : '');
    state.checkoutStatus.textContent = message;
    state.checkoutStatus.hidden = !message;
  }

  function checkoutClick(event, state) {
    event.preventDefault();
    event.stopImmediatePropagation();
    observeMainChange(state, false);
    if (checkoutBusy(state) || unsupportedPlan(state)) return;
    if (state.uncertainVersion === state.selectionVersion) return goToCheckout(state, 'cart');
    if (state.phase === 'cart' || state.addedVersion === state.selectionVersion) {
      const amounts = cartAmounts(state);
      if (amounts && amounts.count > 0 && !state.cartFailed) return goToCheckout(state);
      state.checkoutIntent = { version: state.selectionVersion };
      readCart(state, true);
      syncCheckout(state);
      return;
    }
    if (!mainSelection(state) || state.button.disabled || state.button.classList.contains('disabled') || !state.form.reportValidity()) return;
    state.checkoutMessage = '';
    state.checkoutIntent = { version: state.selectionVersion };
    // Use Turbo's existing synchronous serialization and request exactly once.
    // Do not disable the native button or controls before its click handler runs.
    state.button.click();
    if (!state.attempt && !state.inFlight) state.checkoutIntent = null;
    syncCheckout(state);
  }

  function attachCheckout(state) {
    const host = state.button.closest('.purchase-details__buttons');
    if (!host) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.classList.add('global-button');
    button.classList.add('bpl-checkout-button');
    button.setAttribute('data-bpl-checkout', '');
    button.textContent = 'Add & checkout';
    const note = document.createElement('p');
    note.classList.add('bpl-checkout-status');
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');
    note.hidden = true;
    state.button.insertAdjacentElement('afterend', button);
    button.insertAdjacentElement('afterend', note);
    host.classList.add('bpl-checkout-buttons');
    const layout = host.closest('.purchase-details');
    if (layout) {
      layout.classList.add('bpl-checkout-layout');
      layout.classList.toggle('bpl-checkout-with-quantity', Boolean(layout.querySelector('.purchase-details__quantity')));
    }
    state.checkoutButton = button;
    state.checkoutStatus = note;
    state.checkoutHost = host;
    state.checkoutLayout = layout;
  }

  function mainSelection(state) {
    const id = state.master.value;
    const variant = state.product.variants.find(item => String(item.id) === id);
    const quantityInput = state.form.querySelector('[name="quantity"]');
    const quantity = quantityInput ? integer(quantityInput.value, 1) : 1;
    if (!variant || !variant.available || integer(variant.price) === null || quantity === null) return null;
    if (quantityInput) {
      const min = quantityInput.getAttribute('min');
      const max = quantityInput.getAttribute('max');
      if ((min !== null && quantity < Number(min)) || (max !== null && quantity > Number(max))) return null;
    }
    return { variant, quantity, price: Number(variant.price) };
  }

  function selected(state) {
    return state.addons.filter(item => item.input.checked && !item.input.disabled);
  }

  function mainProperties(state) {
    const properties = [];
    for (const [name, value] of new FormData(state.form).entries()) {
      if (!name.startsWith('properties[')) continue;
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      if (!match || typeof value !== 'string') return null;
      if (value !== '') properties.push([match[1], value]);
    }
    return properties.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
  }

  function mainIdentity(state) {
    if (state.prepared) return state.prepared.plan.identity;
    const quantity = state.form.querySelector('[name="quantity"]');
    const plans = Array.from(state.form.querySelectorAll('[name="selling_plan"]')).filter(control => !control.disabled).map(control => control.value);
    return JSON.stringify([String(state.master.value), quantity ? String(quantity.value) : '1', mainProperties(state), plans]);
  }

  function observeMainChange(state, defer = true) {
    const identity = mainIdentity(state);
    if (identity !== state.mainIdentity) {
      state.mainIdentity = identity;
      editSelection(state, true);
      schedule(state);
    }
    // Variant/quantity widgets may update the native controls later in this event turn.
    if (defer && !state.mainEditTimer) state.mainEditTimer = window.setTimeout(() => {
      state.mainEditTimer = 0;
      if (states.has(state.panel)) observeMainChange(state, false);
    }, 0);
  }

  function pendingPlan(state) {
    const main = mainSelection(state);
    if (!main) return null;
    const extras = selected(state);
    if (extras.length && (!cart || state.cartFailed || pendingMutations.size || !cartAmounts(state))) return null;
    const properties = mainProperties(state);
    const existingMain = properties !== null && cart && cart.items.some(item => {
      if (String(item.variant_id || item.id) !== String(main.variant.id) || integer(item.quantity, 1) === null || item.selling_plan_allocation) return false;
      const entries = Object.entries(item.properties || {}).filter(([, value]) => value !== '' && value !== null)
        .map(([name, value]) => [name, String(value)]).sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
      return JSON.stringify(entries) === JSON.stringify(properties);
    });
    const mainIncluded = !extras.length || state.forceMain || !existingMain;
    const items = (mainIncluded ? [{ id: main.variant.id, quantity: main.quantity }] : [])
      .concat(extras.map(item => ({ id: item.id, quantity: 1 })));
    return { main, extras, mainIncluded, existingMain: Boolean(existingMain), items, properties, identity: mainIdentity(state),
      count: (mainIncluded ? main.quantity : 0) + extras.length,
      original: (mainIncluded ? main.price * main.quantity : 0) + extras.reduce((sum, item) => sum + item.price, 0) };
  }

  function configuredRate(state, count) {
    return Math.max(count >= state.low.count ? state.low.rate : 0, count >= state.high.count ? state.high.rate : 0);
  }

  function supportedDiscounts(value, rate) {
    const applications = Array.isArray(value.cart_level_discount_applications) ? value.cart_level_discount_applications : [];
    if (applications.length > 1) return false;
    if (!applications.length && (Number(value.total_discount) > 0 || (value.item_count > 0 && integer(value.total_discount) === null))) return false;
    for (const application of applications) {
      const percent = Number(application.value);
      // Native automatic order discounts can report a blank type. Require the
      // observed allocation metadata as well as the percentage/target checks.
      const knownType = ['automatic', 'discount_code'].includes(application.type) || (application.type === '' && application.allocation_method === 'across' && integer(application.total_allocated_amount) !== null);
      if (!knownType || application.value_type !== 'percentage' || application.target_type !== 'line_item' || application.target_selection !== 'all' || application.value === null || application.value === '' || !Number.isFinite(percent) || percent < 0 || percent > rate) return false;
      if (application.allocation_method && application.allocation_method !== 'across') return false;
      const combinations = application.combines_with || application.combinesWith;
      if (combinations && Object.values(combinations).some(Boolean)) return false;
    }
    if (value.items.some(item => item.gift_card || item.selling_plan_allocation || Number(item.line_level_total_discount) > 0 || (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length > 0))) return false;
    const knownTitles = new Set(applications.map(application => String(application.title || '').trim().toLowerCase()));
    if (value.items.some(item => Array.isArray(item.discounts) && item.discounts.some(discount => Number(discount.amount) > 0 && !knownTitles.has(String(discount.title || '').trim().toLowerCase())))) return false;
    if (Array.isArray(value.discount_codes) && value.discount_codes.some(code => code.applicable !== false && !knownTitles.has(String(code.code || '').trim().toLowerCase()))) return false;
    return true;
  }

  function confirmedDiscount(state, count) {
    return Boolean(cart && (!cart.currency || cart.currency === state.currency) && supportedDiscounts(cart, configuredRate(state, count)));
  }

  function tier(state, count) {
    return confirmedDiscount(state, count) ? configuredRate(state, count) : 0;
  }

  function cartAmounts(state) {
    if (!cart || cart.currency !== state.currency || typeof cart.item_count !== 'number' || integer(cart.item_count) === null) return null;
    const fields = ['original_total_price', 'items_subtotal_price', 'total_discount', 'total_price'];
    if (fields.some(name => typeof cart[name] !== 'number' || integer(cart[name]) === null)) return null;
    if (cart.original_total_price - cart.total_discount !== cart.total_price || cart.items_subtotal_price > cart.original_total_price || cart.items_subtotal_price < cart.total_price) return null;
    let count = 0;
    let original = 0;
    const lines = [];
    for (const item of cart.items) {
      if (typeof item.quantity !== 'number' || integer(item.quantity, 1) === null) return null;
      if (typeof item.original_line_price !== 'number' || integer(item.original_line_price) === null) return null;
      count += item.quantity;
      original += item.original_line_price;
      if (!Number.isSafeInteger(count) || !Number.isSafeInteger(original)) return null;
      lines.push({ title: item.title, quantity: item.quantity, original: item.original_line_price });
    }
    if (count !== cart.item_count || original !== cart.original_total_price || (count === 0 && cart.original_total_price !== 0)) return null;
    return { count, original: cart.original_total_price, saving: cart.total_discount, total: cart.total_price, lines };
  }

  function quote(state, main, extras) {
    const existing = cartAmounts(state);
    if (!existing) return null;
    if (state.phase === 'cart') {
      const application = Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications[0] : null;
      const rate = application && supportedDiscounts(cart, 100) ? Number(application.value) : 0;
      return { ...existing, cartCount: existing.count, cartOriginal: existing.original, rate, inCart: true };
    }
    const plan = pendingPlan(state);
    if (!plan) return null;
    const count = existing.count + plan.count;
    const original = existing.original + plan.original;
    if (integer(count) === null || integer(original) === null || !confirmedDiscount(state, count)) return null;
    const rate = configuredRate(state, count);
    const saving = Math.floor(original * rate / 100);
    return { count, original, rate, saving, total: original - saving, cartCount: existing.count, cartOriginal: existing.original, lines: existing.lines, inCart: false };
  }

  function unsupportedPlan(state) {
    return Array.from(state.form.querySelectorAll('[name="selling_plan"]')).some(control => !control.disabled && control.value);
  }

  function syncOffer(state, main, extras, known) {
    // Only advertise thresholds that improve on every earlier eligible rate.
    // This follows the existing quote when merchant rates are zero, equal, or reversed.
    const tiers = [];
    [state.low, state.high].sort((a, b) => a.count - b.count || b.rate - a.rate).forEach(item => {
      if (item.rate > (tiers.length ? tiers[tiers.length - 1].rate : 0)) tiers.push(item);
    });
    const show = Boolean(main && known && !state.error && !state.cartFailed && !state.addFailed && tiers.length);
    visible(state.panel, 'offer', show);
    visible(state.panel, 'offer-tiers', show);
    ['low', 'high'].forEach((name, index) => {
      const item = show ? tiers[index] : null;
      const next = item ? tiers[index + 1] : null;
      const quantity = item ? next ? (next.count === item.count + 1 ? String(item.count) : `${item.count}–${next.count - 1}`) : `${item.count}+` : '';
      text(state.panel, `tier-${name}-count`, item ? `${quantity} items` : '');
      text(state.panel, `tier-${name}-rate`, item ? `${item.rate}% off your entire order` : '');
      visible(state.panel, `offer-tier-${name}`, Boolean(item));
    });
  }

  function itemTitle(value, fallback) {
    return typeof value === 'string' && value.trim() ? value.trim().replace(/\s+/g, ' ') : fallback;
  }

  function addonTitle(item) {
    const row = item.input.closest('[data-bpl-addon-row]');
    const name = row && row.querySelector('.bpl-addons__name');
    if (!name) return 'Selected extra';
    // A native block variant span may directly follow the product text node.
    // Preserve both complete pieces with a readable boundary between them.
    const children = Array.from(name.childNodes || []).filter(node => node.nodeType === 1 || node.nodeType === 3);
    return itemTitle(children.length ? children.map(node => node.textContent).join(' ') : name.textContent, 'Selected extra');
  }

  function renderItemLines(state, name, items) {
    const container = state.panel.querySelector(`[data-bpl-${name}]`);
    if (!container) return;
    items.forEach((item, index) => {
      let row = container.children[index];
      if (!row) {
        row = document.createElement('div');
        row.classList.add('bpl-addons__item-line');
        const title = document.createElement('span');
        title.classList.add('bpl-addons__item-name');
        const amount = document.createElement('span');
        amount.classList.add('bpl-addons__money');
        row.appendChild(title);
        row.appendChild(amount);
        container.appendChild(row);
      }
      const title = row.querySelector('.bpl-addons__item-name');
      const amount = row.querySelector('.bpl-addons__money');
      if (!title || !amount) return;
      const label = `${itemTitle(item.title, 'Product')} × ${item.quantity}`;
      const price = money(state, item.original);
      if (title.textContent !== label) title.textContent = label;
      if (amount.textContent !== price) amount.textContent = price;
    });
    while (container.children.length > items.length) container.children[container.children.length - 1].remove();
  }

  function syncBreakdown(state, main, plan) {
    const updating = Boolean(state.inFlight || state.attempt || pendingMutations.size || state.confirmation);
    const actual = !state.cartFailed && !updating ? cartAmounts(state) : null;
    const adding = Boolean(main && plan && state.phase !== 'cart' && !updating && !state.error && !state.addFailed);
    visible(state.panel, 'adding-group', adding);
    let lines = [];
    if (adding) {
      const title = itemTitle(state.product.title, 'Product');
      const variant = itemTitle(main.variant.title, '');
      lines = (plan.mainIncluded ? [{ title: variant && variant !== 'Default Title' ? `${title} — ${variant}` : title, quantity: main.quantity, original: main.price * main.quantity }] : [])
        .concat(plan.extras.map(item => ({ title: addonTitle(item), quantity: 1, original: item.price })));
    }
    renderItemLines(state, 'adding-lines', lines);
    const existing = Boolean(actual && actual.count > 0);
    const empty = Boolean(actual && actual.count === 0);
    visible(state.panel, 'cart-group', true);
    text(state.panel, 'cart-heading', existing ? `In your cart · ${actual.count} ${actual.count === 1 ? 'item' : 'items'}` : 'In your cart');
    text(state.panel, 'cart-subtotal', existing ? money(state, actual.original) : '');
    renderItemLines(state, 'cart-lines', existing ? actual.lines : []);
    text(state.panel, 'cart-empty', empty ? 'No items added yet.' : '');
    visible(state.panel, 'cart-empty', empty);
    const note = actual ? '' : state.cartFailed ? 'Cart details are unavailable. Please refresh the page.'
      : updating ? 'Updating your cart…' : cart ? 'Cart details are unavailable. Please refresh the page.' : 'Loading your cart…';
    text(state.panel, 'cart-note', note);
    visible(state.panel, 'cart-note', Boolean(note));
  }

  function primaryLabel(state, plan, extras) {
    if (extras.length && plan) {
      if (!plan.mainIncluded && extras.length === 1) {
        const title = addonTitle(extras[0]);
        if (/\b(?:lube|lubricant)\b/i.test(title)) return 'Add lube to cart';
        if (/\bcleaner\b/i.test(title)) return 'Add cleaner to cart';
      }
      return `Add ${plan.count} ${plan.count === 1 ? 'item' : 'items'} to cart`;
    }
    return state.phase === 'cart' && plan && plan.existingMain && !state.forceMain ? 'Add another to cart' : state.originalLabel;
  }

  function sync(state) {
    if (!state.panel.isConnected) return;
    // A fresh page can already contain this exact product in the cart. Display the
    // actual order until the customer changes the main selection or chooses extras.
    // The native primary button still explicitly adds another main product.
    if (cart && !state.cartFailed && !pendingMutations.size && !state.inFlight && !state.attempt && !selected(state).length && !state.forceMain && cartAmounts(state)) {
      const current = pendingPlan(state);
      if (current && current.existingMain) {
        state.phase = 'cart';
        state.addedVersion = state.selectionVersion;
        state.cartAdopted = true;
      } else if (state.cartAdopted) {
        state.phase = 'pending';
        state.addedVersion = null;
        state.cartAdopted = false;
      }
    }
    syncCheckout(state);
    if (unsupportedPlan(state)) {
      state.panel.hidden = true;
      state.addons.forEach(item => { item.input.checked = false; });
      state.form.classList.remove('bpl-addons-has-selection');
      state.form.classList.remove('bpl-addons-extras-only');
      state.root.classList.remove('bpl-addons-active');
      if (!state.button.disabled && state.buttonText.textContent === state.lastLabel) state.buttonText.textContent = state.originalLabel;
      state.lastLabel = state.originalLabel;
      return;
    }
    if (state.checkoutOnly) {
      state.form.classList.remove('bpl-addons-extras-only');
      state.panel.hidden = !state.error;
      return;
    }
    state.panel.hidden = false;
    const extras = selected(state);
    state.form.classList.toggle('bpl-addons-has-selection', extras.length > 0);
    state.root.classList.add('bpl-addons-active');
    state.addons.forEach(item => {
      const row = item.input.closest('[data-bpl-addon-row]');
      if (row) row.classList.toggle('bpl-addon-is-selected', item.input.checked && !item.input.disabled);
    });
    const main = mainSelection(state);
    const plan = pendingPlan(state);
    state.form.classList.toggle('bpl-addons-extras-only', Boolean(plan && extras.length && !plan.mainIncluded));
    const known = Boolean(main && plan && confirmedDiscount(state, (cart ? cart.item_count : 0) + (state.phase === 'cart' ? 0 : plan.count)));
    syncOffer(state, main, extras, known);
    const result = main ? quote(state, main, extras) : null;
    const showAmounts = Boolean(main && result && !state.error && !state.cartFailed && !state.addFailed && !pendingMutations.size && !state.inFlight && !state.attempt && !state.confirmation);
    visible(state.panel, 'summary', true);
    syncBreakdown(state, main, plan);
    const showDiscount = Boolean(showAmounts && result.saving > 0);
    visible(state.panel, 'discount-row', showDiscount);
    visible(state.panel, 'subtotal-row', showAmounts);
    visible(state.panel, 'total-row', showAmounts);
    text(state.panel, 'discount', showDiscount ? (result.rate > 0 ? result.inCart ? `Order discount · ${result.rate}%` : `${result.rate}% discount after adding` : 'Discounts') : '');
    text(state.panel, 'saving', showDiscount ? `−${money(state, result.saving)}` : '');
    text(state.panel, 'subtotal', showAmounts ? money(state, result.original) : '');
    text(state.panel, 'total', showAmounts ? money(state, result.total) : '');
    text(state.panel, 'total-label', showAmounts ? (result.inCart ? 'Total' : 'Total after adding') : '');
    const pricingNote = showAmounts ? '' : !main ? 'Choose an available product option and quantity to see your total.'
      : state.addFailed ? 'Please review your cart before adding more items.'
      : state.error ? 'Update your selection to see the order total.'
      : !cart || state.cartFailed || pendingMutations.size || state.inFlight || state.attempt || state.confirmation ? 'Your total will appear once your cart is updated.'
      : 'Final discounts and total will be confirmed in your cart.';
    text(state.panel, 'pricing-note', pricingNote);
    visible(state.panel, 'pricing-note', Boolean(pricingNote));
    if (!main) {
      if (extras.length) status(state, 'Choose an available product option and quantity.');
      return;
    }
    if (!state.error) status(state, '');
    if (!state.button.disabled && !state.button.classList.contains('disabled')) {
      const label = primaryLabel(state, plan, extras);
      if (state.buttonText.textContent !== label) state.buttonText.textContent = label;
      state.lastLabel = label;
    }
  }

  function schedule(state) {
    if (!states.has(state.panel) || state.frame) return;
    state.frame = window.requestAnimationFrame(() => { state.frame = 0; sync(state); });
  }

  function stop(event, state, message) {
    event.preventDefault();
    event.stopImmediatePropagation();
    state.error = message;
    status(state, message);
    sync(state);
  }

  function cleanupPayload(state) {
    if (!state.prepared) return;
    state.prepared.controls.forEach(entry => { entry.control.disabled = entry.disabled; });
    state.prepared.container.remove();
    state.prepared = null;
    if (state.cleanupTimer) window.clearTimeout(state.cleanupTimer);
    state.cleanupTimer = 0;
  }

  function prepare(event, state) {
    observeMainChange(state, false);
    const extras = selected(state);
    if (!extras.length) return;
    if (state.prepared || state.button.disabled || state.button.classList.contains('disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const main = mainSelection(state);
    if (!main || unsupportedPlan(state)) return stop(event, state, 'Choose an available product option and quantity.');
    const plan = pendingPlan(state);
    if (!plan) {
      state.awaitingCart = true;
      readCart(state, true);
      return stop(event, state, 'Checking your cart. Please try again in a moment.');
    }
    if (!state.form.reportValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    const original = new FormData(state.form);
    const ids = original.getAll('id');
    const quantities = original.getAll('quantity');
    if (ids.length !== 1 || String(ids[0]) !== String(main.variant.id) || quantities.length > 1) {
      return stop(event, state, 'Please refresh the page before adding these items.');
    }
    const values = plan.mainIncluded ? [['items[0][id]', String(main.variant.id)], ['items[0][quantity]', String(main.quantity)]] : [];
    for (const [name, value] of original.entries()) {
      if (!name.startsWith('properties[')) continue;
      if (typeof value !== 'string' || !/^properties\[[^\]]+\]$/.test(name)) {
        return stop(event, state, 'Please add the main product separately to keep its custom details.');
      }
      if (plan.mainIncluded) values.push([`items[0][properties]${name.slice('properties'.length)}`, value]);
    }
    extras.forEach((item, index) => {
      const position = index + (plan.mainIncluded ? 1 : 0);
      values.push([`items[${position}][id]`, item.id], [`items[${position}][quantity]`, '1']);
    });
    const container = document.createElement('div');
    container.hidden = true;
    container.setAttribute('data-bpl-addon-payload', '');
    values.forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden'; input.name = name; input.value = value;
      container.appendChild(input);
    });
    const controls = Array.from(state.form.elements)
      .filter(control => control.name === 'id' || control.name === 'quantity' || control.name === 'selling_plan' || String(control.name || '').startsWith('properties['))
      .map(control => ({ control, disabled: control.disabled }));
    controls.forEach(entry => { entry.control.disabled = true; });
    state.form.appendChild(container);
    state.prepared = { container, controls, plan };
    state.error = '';
    status(state, '');
    // A microtask may execute between native event listeners. A task restores only
    // after Turbo's document click listener has synchronously serialized the form.
    state.cleanupTimer = window.setTimeout(() => cleanupPayload(state), 0);
  }

  function attach(panel) {
    if (states.has(panel)) return schedule(states.get(panel));
    const form = panel.closest('form');
    const wrapper = form && form.closest('[data-product-form]');
    const button = form && form.querySelector('.add_to_cart.ajax-submit');
    const master = form && form.querySelector('select[name="id"]');
    if (!form || !wrapper || !button || !master || !window.jQuery || !window.fetch || !window.PXUTheme || !window.PXUTheme.theme_settings || window.PXUTheme.theme_settings.cart_action !== 'ajax' || document.body.classList.contains('cart')) return;
    let product;
    try { product = JSON.parse(wrapper.dataset.product); } catch (_) { return; }
    if (!Array.isArray(product.variants) || product.requires_selling_plan || product.gift_card || (Array.isArray(product.selling_plan_groups) && product.selling_plan_groups.length > 0)) return;
    const buttonText = button.querySelector('span.text');
    if (!buttonText) return;
    const mainIds = new Set(product.variants.map(item => String(item.id)));
    const seen = new Set();
    const inputs = Array.from(panel.querySelectorAll('input[type="checkbox"][data-bpl-addon]'));
    const addons = [];
    for (const input of inputs) {
      const id = input.dataset.id;
      const price = integer(input.dataset.price);
      if (!/^\d+$/.test(id || '') || price === null || seen.has(id) || mainIds.has(id)) return;
      seen.add(id);
      addons.push({ input, id, price, originalDisabled: input.disabled, editVersion: 0 });
    }
    const checkoutOnly = panel.getAttribute('data-bpl-checkout-only') !== null;
    if (!addons.length && !checkoutOnly) return;
    const low = { count: integer(panel.getAttribute('data-tier-2-count') || 2, 1), rate: integer(panel.getAttribute('data-tier-2-rate') || 10) };
    const high = { count: integer(panel.getAttribute('data-tier-3-count') || 3, 1), rate: integer(panel.getAttribute('data-tier-3-rate') || 15) };
    if (low.count === null || high.count === null || low.rate === null || high.rate === null || low.rate > 100 || high.rate > 100) return;
    const currency = panel.dataset.currency || wrapper.dataset.shopCurrency || 'USD';
    if (!/^[A-Z]{3}$/.test(currency)) return;
    const root = panel.closest('.bpl-product--01');
    const state = { panel, root, form, wrapper, button, buttonText, master, product, addons, low, high, currency, frame: 0, prepared: null, cleanupTimer: 0, error: '', cartFailed: false, phase: 'pending', selectionVersion: 0, attempt: null, attemptTimer: 0, confirmation: null, addFailed: false, originalLabel: button.getAttribute('data-label') || buttonText.textContent.trim(), lastLabel: '' };
    Object.assign(state, { checkoutOnly, inFlight: null, checkoutIntent: null, addedVersion: null, uncertainVersion: null, navigating: false, checkoutMessage: '', forceMain: false, mainRevision: 0, awaitingCart: false, cartAdopted: false });
    state.mainIdentity = mainIdentity(state);
    state.mainEditTimer = 0;
    // Liquid may serialize the native label as JSON; prefer the existing visible label.
    state.originalLabel = state.originalLabel.replace(/^"|"$/g, '');
    addons.forEach(item => { item.input.disabled = item.originalDisabled || item.input.dataset.available !== 'true'; });
    state.change = event => {
      if (event.target.matches('[data-bpl-addon], [name="id"], [name="quantity"], [name="selling_plan"], .single-option-selector, .swatch input[type="radio"]') || String(event.target.name || '').startsWith('properties[')) {
        const addon = state.addons.find(item => item.input === event.target);
        if (addon) { addon.editVersion += 1; editSelection(state); }
        else observeMainChange(state);
        schedule(state);
      }
    };
    state.click = event => {
      const target = event.target.closest ? event.target : event.target.parentElement;
      if (!target) return;
      if (state.checkoutButton && target.closest('[data-bpl-checkout]') === state.checkoutButton) return checkoutClick(event, state);
      if (target.closest('.js-change-quantity')) observeMainChange(state);
      if (target.closest('.add_to_cart.ajax-submit') === button) {
        if (checkoutBusy(state) || state.uncertainVersion === state.selectionVersion) {
          event.preventDefault(); event.stopImmediatePropagation(); return;
        }
        prepare(event, state); armAttempt(state, event);
      }
    };
    state.submit = event => {
      observeMainChange(state, false);
      if (checkoutBusy(state) || state.uncertainVersion === state.selectionVersion) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!selected(state).length) { armAttempt(state, event); return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!button.disabled && !button.classList.contains('disabled')) button.click();
    };
    form.addEventListener('change', state.change, true);
    form.addEventListener('input', state.change, true);
    form.addEventListener('click', state.click, true);
    form.addEventListener('submit', state.submit, true);
    states.set(panel, state);
    attachCheckout(state);
    if (window.MutationObserver) {
      state.buttonObserver = new window.MutationObserver(() => schedule(state));
      state.buttonObserver.observe(button, { attributes: true, attributeFilter: ['disabled', 'class'], childList: true, subtree: true });
    }
    panel.setAttribute('data-bpl-addons-ready', '');
    bindCartHook();
    sync(state);
    readCart(state, true);
  }

  function init(root) {
    if (root.matches && root.matches(selector)) attach(root);
    root.querySelectorAll(selector).forEach(attach);
  }

  function unload(root) {
    states.forEach((state, panel) => {
      if (root !== panel && !root.contains(panel)) return;
      cleanupPayload(state);
      clearAttempt(state);
      if (state.mainEditTimer) window.clearTimeout(state.mainEditTimer);
      state.checkoutIntent = null;
      if (state.checkoutButton) state.checkoutButton.remove();
      if (state.checkoutStatus) state.checkoutStatus.remove();
      if (state.checkoutHost) state.checkoutHost.classList.remove('bpl-checkout-buttons');
      if (state.checkoutLayout) {
        state.checkoutLayout.classList.remove('bpl-checkout-layout');
        state.checkoutLayout.classList.remove('bpl-checkout-with-quantity');
      }
      if (state.frame) window.cancelAnimationFrame(state.frame);
      state.form.removeEventListener('change', state.change, true);
      state.form.removeEventListener('input', state.change, true);
      state.form.removeEventListener('click', state.click, true);
      state.form.removeEventListener('submit', state.submit, true);
      if (state.buttonObserver) state.buttonObserver.disconnect();
      state.addons.forEach(item => { item.input.disabled = item.originalDisabled; });
      if (state.buttonText.textContent === state.lastLabel) state.buttonText.textContent = state.originalLabel;
      panel.hidden = true;
      panel.removeAttribute('data-bpl-addons-ready');
      state.form.classList.remove('bpl-addons-has-selection');
      state.form.classList.remove('bpl-addons-extras-only');
      state.root.classList.remove('bpl-addons-active');
      states.delete(panel);
    });
    if (!states.size && cartHook && window.jQuery) {
      window.jQuery(document).off('.bplProductAddons');
      cartHook = false;
      refreshAfterRequest = false;
      pendingMutations.clear();
      cartRevision += 1;
      cart = null;
    }
  }

  window.BPLProductAddons = { init, unload };
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  window.addEventListener('load', () => init(document), { once: true });
  window.addEventListener('focus', () => { if (states.size) readCart(states.values().next().value); });
  window.addEventListener('pageshow', () => {
    states.forEach(state => { state.navigating = false; state.checkoutIntent = null; schedule(state); });
    if (states.size) readCart(states.values().next().value, true);
  });
})();
